import { Component } from '../Component'
import { element } from '../CustomElement'
import { Page } from '../flipp-page/Page'
import { i18n } from '../../i18n/I18n'
import { AuthenticationService } from '../../services/AuthenticationService'
import { inject } from '../../services/di'
import { ConfigService } from '../../services/ConfigService'
import { Country, LocationService } from '../../services/LocationService'
import { AnalyticsService } from '../../services/AnalyticsService'
import { AccountsService } from '../../services/AccountsService'
import { AuthStatus } from '../../services/definitions/IAuthenticationService'
import { EventService } from '../../services/EventService'
import { LinkService } from '../../services/LinkService'
import { Dropdown } from '../flipp-dropdown/Dropdown'
import { cppa_states } from './ccpa_states'
import { icons } from '../../icons/Icons'
import { sanitize } from '../../utils/sanitize'

@element('flipp-data-form')
export class CCPAForm extends Component<any, void> {
    @inject auth: AuthenticationService
    @inject config: ConfigService
    @inject location: LocationService
    @inject analytics: AnalyticsService
    @inject fetchUserData: AccountsService
    @inject eventService: EventService
    @inject linkService: LinkService

    refs: {
        formInputNameFirst: HTMLInputElement
        formInputNameLast: HTMLInputElement
        formInputEmail: HTMLInputElement
        radioReceive: HTMLInputElement
        radioNotSell: HTMLInputElement
        submitForm: HTMLFormElement
        submitBtn: HTMLInputElement
        dataFormWrapper: HTMLDivElement
        spinner: HTMLElement
        canadaChipRef: HTMLElement
        usaChipRef: HTMLElement
        selectStateDropdownRef: Dropdown
        errorMessage: HTMLDivElement
        errorText: HTMLDivElement
    }

    state = {
        dataAction: 'notSell',
        firstName: '',
        lastName: '',
        email: '',
        residentOf: '',
        access_token: '',
        user_id: '',
        sid: '',
        fsa: '',
        application_version: '',
        locale: '',
        country_code: '',
    }

    onAttach() {
        const form = this.refs.submitForm
        const elements = form.querySelectorAll('input, select')

        elements.forEach((element) => {
            element.addEventListener('change', this.handleChange)
        })
        window.addEventListener('redirect-from-form', this.changeUserLocation)
        window.addEventListener('flipp-auth', this.checkEmail)
        window.addEventListener('redirect-logout-form', this.changeUserLocation)
        this.state.dataAction = ''
        this.state.firstName = ''
        this.state.lastName = ''
        this.state.email = ''
        this.state.residentOf = ''
        this.checkSignIn()
        this.checkEmail()
        this.setSelectedCountry(this.getCountry())
    }

    onDetach() {
        const form = this.refs.submitForm
        const elements = form.querySelectorAll('input, select')

        elements.forEach((element) => {
            element.removeEventListener('change', this.handleChange)
        })
        window.removeEventListener(
            'redirect-from-form',
            this.changeUserLocation
        )
        window.removeEventListener('flipp-auth', this.checkEmail)
        window.removeEventListener(
            'redirect-logout-form',
            this.changeUserLocation
        )
    }

    private handleSubmit = async (e: any) => {
        e.preventDefault()
        this.refs.spinner.classList.add('not-hide')
        this.refs.dataFormWrapper.classList.add('transparent')

        try {
            const res: Response =
                this.state.dataAction === 'notSell'
                    ? await this.fetchUserData.notSellData(this.state)
                    : await this.fetchUserData.receiveCopyData(this.state)

            if (!res.ok) {
                this.handleSubmitError(i18n('my_data_server_error_message'))
                return
            }

            this.handleSuccessfulResponse()
        } catch (err) {
            if (!navigator.onLine) {
                this.handleSubmitError(i18n('my_data_nonetwork_error_message'))
            } else {
                this.handleSubmitError(i18n('my_data_server_error_message'))
            }
        } finally {
            this.refs.spinner.classList.remove('not-hide')
            this.refs.dataFormWrapper.classList.remove('transparent')
        }
    }

    private handleChange = (e: any) => {
        Object.assign(this.state, {
            [e.target.name]: sanitize(e.target.value),
        })
        const allFieldsFilled = [
            this.state.firstName,
            this.state.lastName,
            this.state.email,
            this.state.residentOf,
            this.state.dataAction,
        ].every((field) => field && field.trim() !== '')

        const isActive = this.refs.submitBtn.classList.contains('active')
        if (allFieldsFilled && !isActive) {
            this.refs.submitBtn.classList.add('active')
            this.refs.submitBtn.removeAttribute('disabled')
        } else if (!allFieldsFilled && isActive) {
            this.refs.submitBtn.classList.remove('active')
            this.refs.submitBtn.setAttribute('disabled', 'disabled')
        }
    }

    private handleCountryClick(country: Country) {
        this.setSelectedCountry(country)
        const options = this.getSelectStateOptions(country)
        this.refs.selectStateDropdownRef.setAttribute('options', options)
        this.handleChange({ target: { name: 'residentOf', value: '' } })
    }

    private handleItemSelected(value: string) {
        this.state.residentOf = value
        this.handleChange({ target: { name: 'residentOf', value } })
    }

    private handleSuccessfulResponse() {
        this.refs.spinner.classList.remove('not-hide')
        this.refs.dataFormWrapper.classList.remove('transparent')
        this.linkService.execute('/data/success', this)
    }

    private handleUnauthorizedUser = () => {
        this.linkService.execute('/signin#data', this)
    }

    private handleSubmitError = (errMessage: string) => {
        this.refs.errorText.textContent = errMessage
        this.refs.errorMessage.style.display = 'flex'
    }

    setSelectedCountry(country: Country) {
        if (country === Country.USA) {
            this.refs.usaChipRef.classList.add('selected')
            this.refs.canadaChipRef.classList.remove('selected')
        } else if (country === Country.CANADA) {
            this.refs.usaChipRef.classList.remove('selected')
            this.refs.canadaChipRef.classList.add('selected')
        }
    }

    getSelectStateOptions(selectedCountry?: Country) {
        const detectedCountry = this.getCountry()
        const country =
            selectedCountry !== undefined ? selectedCountry : detectedCountry
        const isFrench = this.location.locale === 'fr-ca'
        const states = cppa_states
            .filter((obj) => obj.identifier === country)[0]
            .states.map((state) => {
                return {
                    name: isFrench
                        ? state.display_name['fr']
                        : state.display_name['en'],
                    value: state.identifier,
                }
            })
        return JSON.stringify(states)
    }

    private checkEmail = () => {
        if (
            this.auth.status === AuthStatus.LOGGED_IN &&
            this.auth.user.info.email
        ) {
            const loginUserEmail = `${this.auth.user.info.email}`
            this.refs.formInputEmail.value = loginUserEmail
            this.state.email = loginUserEmail
            this.refs.formInputEmail.setAttribute('disabled', 'disabled')
        }
        this.getToken()
    }

    private getToken = () => {
        if (this.auth.status === AuthStatus.LOGGED_IN) {
            window.removeEventListener(
                'redirect-from-form',
                this.changeUserLocation
            )
            const appVersionArray = this.config.BUILD.split('/')
            const countryCodeArray = this.location.locale.split('-')
            this.state.application_version =
                appVersionArray[appVersionArray.length - 1]
            this.state.sid = this.analytics.sessionId
            this.state.fsa = this.location.postalCode
            this.state.locale = this.location.locale
            this.state.country_code =
                countryCodeArray[countryCodeArray.length - 1].toUpperCase()
            this.state.user_id = this.auth.user.token['user_id']
            this.state.access_token = this.auth.user.token['access_token']
        }
    }

    private changeUserLocation = () => {
        this.linkService.execute('/flyers', this)
    }

    private checkSignIn = () => {
        if (this.auth.status !== AuthStatus.LOGGED_IN) {
            this.handleUnauthorizedUser()
        }
    }

    private getCountry() {
        const info = this.location.getStoredInfo()
        if (info.country_code_by_ip === 'CA') {
            return Country.CANADA
        } else if (info.country_code_by_ip === 'US') {
            return Country.USA
        } else if (info.country_code_by_postal_code === 'US') {
            return Country.USA
        } else {
            return Country.CANADA
        }
    }

    render() {
        const privacyLink =
            this.location.locale === 'fr-ca'
                ? 'https://corp.flipp.com/legal/politique-de-confidentialite'
                : 'https://corp.flipp.com/privacy'
        return (
            <Page show="flyers">
                <section ref="spinner" class="main">
                    <div class="loading">
                        <div class="spinner"></div>
                    </div>
                </section>
                <div ref="dataFormWrapper" class="data-form-wrapper">
                    <h3>{i18n('my_data_title')}</h3>
                    <div class="form-text">{i18n('my_data_text')}</div>
                    <form
                        name="dataForm"
                        onSubmit={this.handleSubmit}
                        class="form-inputs"
                        ref="submitForm"
                    >
                        <input
                            type="text"
                            name="firstName"
                            placeholder={i18n('my_data_name')}
                            ref="formInputNameFirst"
                            pattern="\w{2,16}"
                        />
                        <input
                            type="text"
                            name="lastName"
                            placeholder={i18n('my_data_surname')}
                            ref="formInputNameLast"
                            pattern="\w{2,16}"
                        />
                        <input
                            type="email"
                            name="email"
                            placeholder={i18n('my_data_email')}
                            ref="formInputEmail"
                            maxLength={100}
                            pattern="[a-zA-Z0-9.-_]{1,}@[a-zA-Z.-]{2,}[.]{1}[a-zA-Z]{2,}"
                            value={this.state.email}
                        />

                        <label>{i18n('my_data_select_country')}</label>
                        <div class="select-country-wrapper">
                            <div
                                class="country"
                                ref="canadaChipRef"
                                onClick={() =>
                                    this.handleCountryClick(Country.CANADA)
                                }
                            >
                                Canada
                            </div>
                            <div
                                class="country"
                                ref="usaChipRef"
                                onClick={() =>
                                    this.handleCountryClick(Country.USA)
                                }
                            >
                                USA
                            </div>
                        </div>

                        <label>{i18n('my_data_select_state')}</label>
                        <div class="select-state-wrapper">
                            <Dropdown
                                ref="selectStateDropdownRef"
                                options={this.getSelectStateOptions()}
                                handleItemSelected={this.handleItemSelected.bind(
                                    this
                                )}
                            />
                        </div>
                        <div class="horizontal-divider"></div>
                        <label>{i18n('my_data_select_request')}</label>
                        <div class="radio-group">
                            <div class="radio-option">
                                <input
                                    type="radio"
                                    id="notSell"
                                    name="dataAction"
                                    value="notSell"
                                    ref="radioNotSell"
                                />
                                <label for="notSell">
                                    {i18n(
                                        'my_data_select_option_label_do_not_sell'
                                    )}
                                    <div class="radio-text">
                                        {i18n(
                                            'my_data_select_option_text_do_not_sell'
                                        )}
                                    </div>
                                </label>
                            </div>
                            <div class="radio-option">
                                <input
                                    type="radio"
                                    id="receive"
                                    name="dataAction"
                                    value="receive"
                                    ref="radioReceive"
                                />
                                <label for="receive">
                                    {i18n(
                                        'my_data_select_option_label_receive_copy'
                                    )}
                                    <div class="radio-text">
                                        {i18n(
                                            'my_data_select_option_text_receive_copy'
                                        )}
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div class="disclaimer-text">
                            <p>
                                {i18n('my_data_disclaimer_text1')}{' '}
                                <a href={privacyLink} target="_blank">
                                    {privacyLink}
                                </a>
                                .
                            </p>
                            <p>{i18n('my_data_disclaimer_text2')}</p>
                            <p>{i18n('my_data_disclaimer_text3')}</p>
                        </div>
                        <input
                            class="submit"
                            type="submit"
                            value={i18n('submit')}
                            ref="submitBtn"
                            disabled
                        />
                        <div ref="errorMessage" class="errorMessage">
                            <div class="iconWrapper">{icons.CCPA_error}</div>
                            <div class="errorText" ref="errorText"></div>
                        </div>
                    </form>
                </div>
            </Page>
        )
    }
}
